import React, { useState, useEffect, useCallback } from "react";
import {
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiAlertTriangle,
  FiSmartphone,
  FiShield,
  FiCalendar,
} from "react-icons/fi";
import { userRequest } from "../utils/requestMethods";
import { toast } from "react-toastify";
import { format } from "date-fns";

const ClockingDeviceModal = ({ isOpen, onClose, schoolId, school, refreshSchool }) => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [stationDevice, setStationDevice] = useState(school?.authorised_clocking_device || null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [claimedByOther, setClaimedByOther] = useState(false);

  const fetchCurrentDeviceStatus = useCallback(async () => {
    try {
      setFetching(true);
      setClaimedByOther(false);
      const { data } = await userRequest.get(`/schools/${schoolId}`);
      setStationDevice(data?.authorised_clocking_device || null);
    } catch (error) {
      console.error("Error fetching school device status:", error);
    } finally {
      setFetching(false);
    }
  }, [schoolId]);

  useEffect(() => {
    if (isOpen && schoolId) {
      fetchCurrentDeviceStatus();
    }
  }, [isOpen, schoolId, fetchCurrentDeviceStatus]);

  // Keep in sync with the school context while the modal stays open — e.g. if
  // another tab/admin changes the station device and this session's school
  // context gets refreshed via refreshSchool(), don't keep showing stale status.
  useEffect(() => {
    if (isOpen) {
      setStationDevice(school?.authorised_clocking_device || null);
    }
  }, [isOpen, school]);

  const handleSetupDevice = async () => {
    try {
      setLoading(true);
      setClaimedByOther(false);

      const devicePayload = {
        os: navigator.platform || "Unknown OS",
        user_agent: navigator.userAgent || "Unknown Browser",
      };

      const { data } = await userRequest.post(`/clocking/${schoolId}/device/setup`, {
        device: devicePayload,
      });

      if (data?.alreadyAuthorisedByOther) {
        setClaimedByOther(true);
        toast.warn("A station device is already registered for this school.");
        await fetchCurrentDeviceStatus();
      } else if (data?.authorised) {
        toast.success("This device is now registered as the authorised clocking station!");
        if (refreshSchool) {
          await refreshSchool();
        }
        await fetchCurrentDeviceStatus();
      } else {
        toast.info(data?.message || "Device setup processed.");
        await fetchCurrentDeviceStatus();
      }
    } catch (error) {
      console.error("Error setting up station device:", error);
      toast.error(error.response?.data?.message || "Failed to register clocking station device.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetDevice = async () => {
    try {
      setLoading(true);
      const { data } = await userRequest.post(`/clocking/${schoolId}/device/reset`);
      toast.success(data?.message || "Station device has been reset successfully.");
      setStationDevice(null);
      setConfirmReset(false);
      setClaimedByOther(false);
      if (refreshSchool) {
        await refreshSchool();
      }
    } catch (error) {
      console.error("Error resetting station device:", error);
      toast.error(error.response?.data?.message || "Failed to reset station device.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl flex flex-col max-h-[90vh] shadow-2xl">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 text-indigo-700 rounded-xl flex items-center justify-center flex-shrink-0">
                <FiSmartphone className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-slate-800">
                  Clocking Station Device
                </h2>
                <p className="text-slate-500 text-xs md:text-sm">
                  Manage the authorised on-site tablet/device
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:bg-slate-200 p-2 rounded-lg transition-colors flex-shrink-0"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {fetching ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 mb-3"></div>
              <p className="text-slate-600 text-sm">Checking device authorisation...</p>
            </div>
          ) : (
            <>
              {/* Claimed by other banner */}
              {claimedByOther && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 flex items-start gap-3">
                  <FiAlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold">Another Device is Already Designated</p>
                    <p className="text-amber-700 mt-1">
                      A clocking station has already been registered. To claim this current
                      browser/device instead, reset the active station below.
                    </p>
                  </div>
                </div>
              )}

              {/* Status Card */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {stationDevice ? (
                      <div className="w-9 h-9 bg-green-100 text-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FiCheckCircle className="w-5 h-5" />
                      </div>
                    ) : (
                      <div className="w-9 h-9 bg-amber-100 text-amber-700 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FiAlertCircle className="w-5 h-5" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-slate-800 text-sm md:text-base">
                        {stationDevice
                          ? "Station Device Configured"
                          : "No Station Device Designated"}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {stationDevice
                          ? "Staff ID card clock-ins at the gate will only be accepted from this authorised device."
                          : "Staff without personal devices cannot clock in via ID card until a station device is set up."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Device Details if present */}
                {stationDevice && (
                  <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-slate-400 uppercase tracking-wider block">
                        Operating System
                      </span>
                      <span className="font-medium text-slate-700">
                        {stationDevice.device?.os || stationDevice.device?.platform || "Unknown"}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-400 uppercase tracking-wider block">
                        Model / Browser
                      </span>
                      <span
                        className="font-medium text-slate-700 truncate block"
                        title={stationDevice.device?.user_agent || stationDevice.device?.model}
                      >
                        {stationDevice.device?.model ||
                          (stationDevice.device?.user_agent
                            ? stationDevice.device.user_agent.slice(0, 35) + "…"
                            : "Standard Browser")}
                      </span>
                    </div>
                    {stationDevice.authorised_at && (
                      <div className="flex items-center gap-1.5 text-slate-600 sm:col-span-2 mt-1">
                        <FiCalendar className="text-slate-400" />
                        <span>
                          Authorised on:{" "}
                          {format(new Date(stationDevice.authorised_at), "dd MMM yyyy, hh:mm a")}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Reset Confirmation Dialog */}
              {confirmReset && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <FiAlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900 text-sm">Confirm Station Reset</h4>
                      <p className="text-xs text-red-700 mt-1">
                        Resetting the station device will immediately deactivate clock-ins from the
                        current tablet. Are you sure?
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setConfirmReset(false)}
                      disabled={loading}
                      className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResetDevice}
                      disabled={loading}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {loading ? "Resetting..." : "Yes, Reset Station"}
                    </button>
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex items-start gap-3 text-xs text-indigo-900">
                <FiShield className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold">Security Note:</span> The designated station
                  device represents the school gate station. Once claimed, scanning Staff ID cards
                  will only be accepted from this specific device or a teacher's previously approved
                  personal phone.
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0">
          <div>
            {stationDevice && !confirmReset && (
              <button
                type="button"
                onClick={() => setConfirmReset(true)}
                disabled={loading}
                className="text-red-600 hover:text-red-800 text-xs sm:text-sm font-medium transition-colors"
              >
                Reset Authorised Device
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-100 transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleSetupDevice}
              disabled={loading || fetching}
              className="px-5 py-2 text-xs sm:text-sm font-medium text-white bg-[#3F3DA5] hover:bg-indigo-900 rounded-full shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FiSmartphone className="w-3.5 h-3.5" />
                  <span>Set Up This Device as Station</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClockingDeviceModal;
